import { useEffect, useState } from 'react';
import { remove } from 'aws-amplify/storage';
import { CompanyResource } from '../../types/companyResourceTypes';
import {
    deleteCompanyResource,
    listTrainingSchedulesByTopicId,
    deleteTrainingScheduleEntry,
} from '../../graphql/companyResourceQueries';
import { deleteTrainingTopic, listTrainingPlansByCompany } from '../../graphql/trainingQueries';
import { CustomModal } from '../CustomModal';
import { Btn } from '../ui';

interface DeleteResourceModalProps {
    resource: CompanyResource | null;
    onClose: () => void;
    onDelete: () => Promise<void>;
    companyId: string;
}

interface PlanUsageInfo {
    scheduleEntries: { id: string; planId: string }[];
    affectedPlans: { id: string; name: string; status: string }[];
}

export const DeleteResourceModal = ({ resource, onClose, onDelete, companyId }: DeleteResourceModalProps) => {
    const visible = Boolean(resource?.id);
    const [planUsage, setPlanUsage] = useState<PlanUsageInfo | null>(null);
    const [isChecking, setIsChecking] = useState(true);
    const [acknowledged, setAcknowledged] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteBoth, setDeleteBoth] = useState(true);
    const [confirmReady, setConfirmReady] = useState(false);

    useEffect(() => {
        if (!visible) {
            setConfirmReady(false);
            return;
        }
        setConfirmReady(false);
        const timer = window.setTimeout(() => setConfirmReady(true), 400);
        return () => window.clearTimeout(timer);
    }, [visible, resource?.id]);

    useEffect(() => {
        if (!resource) {
            setPlanUsage(null);
            setIsChecking(true);
            setAcknowledged(false);
            setDeleteBoth(true);
            return;
        }

        let isCancelled = false;
        setIsChecking(true);
        setAcknowledged(false);
        setDeleteBoth(true);

        (async () => {
            if (!resource.trainingTopicId) {
                if (isCancelled) return;
                setPlanUsage({ scheduleEntries: [], affectedPlans: [] });
                setIsChecking(false);
                return;
            }

            try {
                const scheduleEntries = await listTrainingSchedulesByTopicId(resource.trainingTopicId);
                if (isCancelled) return;
                if (scheduleEntries.length === 0) {
                    setPlanUsage({ scheduleEntries: [], affectedPlans: [] });
                } else {
                    const planIds = [...new Set(scheduleEntries.map((s: { planId: string }) => s.planId))];
                    const allPlans = await listTrainingPlansByCompany(companyId);
                    if (isCancelled) return;
                    const affectedPlans = planIds
                        .map(pid => allPlans.find((p) => p.id === pid))
                        .filter((p): p is NonNullable<typeof p> => p != null)
                        .map((p) => ({
                            id: p.id,
                            name: p.name,
                            status: p.status,
                        }));

                    setPlanUsage({ scheduleEntries, affectedPlans });
                }
            } catch (err) {
                console.error('Error checking plan usage:', err);
                if (isCancelled) return;
                setPlanUsage({ scheduleEntries: [], affectedPlans: [] });
            } finally {
                if (!isCancelled) setIsChecking(false);
            }
        })();

        return () => { isCancelled = true; };
    }, [resource, companyId]);

    const handleDismiss = () => {
        if (deleting) return;
        onClose();
    };

    const handleDelete = async () => {
        if (!resource) return;
        setDeleting(true);
        try {
            if (deleteBoth && resource.trainingTopicId) {
                if (planUsage && planUsage.scheduleEntries.length > 0) {
                    for (const entry of planUsage.scheduleEntries) {
                        try {
                            await deleteTrainingScheduleEntry(entry.id);
                        } catch (err) {
                            console.error('Error deleting schedule entry:', err);
                        }
                    }
                }

                try {
                    await deleteTrainingTopic(resource.trainingTopicId);
                } catch (err) {
                    console.error('Error deleting linked training topic:', err);
                }
            }

            if (resource.contentFilePath) {
                try {
                    await remove({ path: resource.contentFilePath });
                } catch (err) {
                    console.error('Error removing file from S3:', err);
                }
            }

            await deleteCompanyResource(resource.id);
            await onDelete();
        } catch (err) {
            console.error('Error deleting resource:', err);
        } finally {
            setDeleting(false);
        }
    };

    const hasPlanUsage = Boolean(planUsage && planUsage.affectedPlans.length > 0);
    const hasActivePlan = planUsage?.affectedPlans.some(p => p.status === 'ACTIVE') ?? false;
    const canDelete = (!hasPlanUsage || acknowledged) && (!hasActivePlan || acknowledged);
    const resourceTitle = resource?.title?.trim() || 'this file';

    return (
        <CustomModal
            onDismiss={handleDismiss}
            visible={visible}
            header="Delete file?"
            footer={
                <div className="modal-footer-actions">
                    <Btn
                        variant="secondary-outlined"
                        buttonText="Cancel"
                        buttonDisabled={deleting}
                        buttonOnClick={handleDismiss}
                    />
                    <Btn
                        variant="danger"
                        buttonText={
                            deleting
                                ? 'Deleting...'
                                : deleteBoth && resource?.trainingTopicId
                                    ? 'Delete Both'
                                    : 'Delete'
                        }
                        buttonDisabled={isChecking || !canDelete || deleting || !confirmReady}
                        buttonOnClick={handleDelete}
                    />
                </div>
            }
        >
            {isChecking ? (
                <p>Checking plan usage...</p>
            ) : (
                <>
                    <p>
                        You are about to permanently delete <strong>“{resourceTitle}”</strong>. This cannot be undone.
                    </p>

                    {resource?.trainingTopicId && (
                        <div className="cr-delete-warning">
                            <p>
                                This resource is linked to a training topic
                                {!hasPlanUsage && ' (unassigned)'}.
                            </p>
                            <label className="cr-delete-checkbox">
                                <input
                                    type="checkbox"
                                    checked={deleteBoth}
                                    onChange={e => setDeleteBoth(e.target.checked)}
                                />
                                Also delete the linked training topic
                            </label>
                        </div>
                    )}

                    {hasPlanUsage && deleteBoth && (
                        <div className="cr-delete-warning">
                            The linked training topic is currently scheduled in{' '}
                            <strong>{planUsage!.affectedPlans.length} training plan(s)</strong>:
                            <ul className="cr-delete-plans-list">
                                {planUsage!.affectedPlans.map(p => (
                                    <li key={p.id}>
                                        {p.name} ({p.status})
                                    </li>
                                ))}
                            </ul>
                            {hasActivePlan && (
                                <p>
                                    One or more plans are ACTIVE. Deleting will remove this topic from active training schedules.
                                </p>
                            )}
                        </div>
                    )}

                    {hasPlanUsage && deleteBoth && (
                        <label className="cr-delete-checkbox">
                            <input
                                type="checkbox"
                                checked={acknowledged}
                                onChange={e => setAcknowledged(e.target.checked)}
                            />
                            I understand this will remove the topic from the listed training plans
                        </label>
                    )}
                </>
            )}
        </CustomModal>
    );
};
